"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection from "./AnimatedSection";
import Image from "next/image";
import { ArrowRight } from "lucide-react";
import { useState } from "react";
import { useRouter } from "next/navigation";
import { EmailInput } from "@/components/ui";

export default function CtaSection() {
  const { t, lang } = useLang();
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");

  const handleSignup = async () => {
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      return;
    }
    setLoading(true);
    try {
      await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, source: "cta", lang }),
      });
      setEmail("");
      router.push(`/signup?email=${encodeURIComponent(trimmed)}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="register" className="pt-24 pb-0 px-6 bg-[#FAFAFA]">
      <div className="max-w-[1200px] mx-auto">
        <AnimatedSection className="bg-[#111] rounded-t-[48px] rounded-b-[24px] p-12 md:p-20 flex flex-col md:flex-row items-center justify-between gap-12 overflow-hidden relative">

          {/* Background glow */}
          <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-redy-orange/8 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2 pointer-events-none" />
          <div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-redy-red/8 rounded-full blur-[80px] translate-y-1/2 -translate-x-1/2 pointer-events-none" />

          {/* Left: food image disk */}
          <div className="hidden md:flex relative w-[220px] h-[300px] bg-redy-orange rounded-full flex-col items-center justify-start pt-8 shrink-0">
            <div className="w-[168px] h-[168px] rounded-full overflow-hidden shadow-2xl border-4 border-white mb-5">
              {/* Servida desde public/: la CSP (img-src) no permite hosts de
                  stock, y la foto de Unsplash que iba aquí ya devolvía 404. Va
                  en la raíz de public/ a propósito: una ruta de dos segmentos
                  (/carpeta/foto.jpg) el proxy la toma por /{slug}/{página}. */}
              <Image
                src="/cta-plato.jpg"
                alt="Delicious Food"
                width={400}
                height={400}
                sizes="168px"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="text-white text-center font-bold">
              <div className="text-[12px] text-white/70">{t("Dual pricing", "Dual pricing")}</div>
              <div className="text-[22px] font-black mt-0.5">{t("Built in", "Incluido")}</div>
            </div>
          </div>

          {/* Center: text + input */}
          <div className="flex-1 text-center relative z-10">
            <h2 className="text-4xl md:text-[52px] font-black tracking-tight leading-[1.05] text-white mb-4">
              {t("Stop paying for", "Deja de pagar por")}
              <br />
              <span className="text-white/35">
                {t("software you don't use", "software que no usas")}
              </span>
            </h2>
            <p className="text-[15px] font-medium text-white/45 mb-10 max-w-md mx-auto">
              {t(
                "Join the restaurants, food trucks, bars and clubs that let card payers cover the processing cost.",
                "Únete a los restaurantes, food trucks, bares y clubs donde quien paga con tarjeta cubre el costo de procesamiento."
              )}
            </p>

            <div className="flex flex-col sm:flex-row gap-3 w-full max-w-[480px] mx-auto">
              <EmailInput
                value={email}
                onChange={setEmail}
                placeholder={t("your@business.com", "tu@negocio.com")}
                disabled={loading}
                inputClassName="flex-1 px-6 py-4 bg-white/10 border border-white/15 rounded-full text-white placeholder-white/25 text-[15px] outline-none focus:border-redy-orange focus:bg-white/15 transition-all disabled:opacity-60"
              />
              <button
                onClick={handleSignup}
                disabled={loading}
                className="group px-8 py-4 bg-redy-orange text-white font-bold rounded-full shadow-lg shadow-redy-orange/25 hover:scale-105 transition-all active:scale-95 whitespace-nowrap flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
              >
                {loading ? t("Sending…", "Enviando…") : t("Get started", "Comenzar")}
                {!loading && <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />}
              </button>
            </div>
          </div>

          {/* Right: North terminal card */}
          <div className="hidden md:flex relative w-[220px] h-[300px] bg-white rounded-full flex-col items-center justify-end pb-8 shrink-0">
            <div className="text-redy-charcoal text-center font-bold mb-5 px-4">
              <div className="text-[12px] text-redy-secondary">{t("Hardware you own", "Hardware que es tuyo")}</div>
              <div className="text-[18px] font-black mt-0.5">{t("North Terminal", "Terminal North")}</div>
            </div>
            <div className="w-[168px] h-[168px] rounded-full overflow-hidden shadow-xl border border-black/5 bg-[#F7F5F1] flex items-center justify-center">
              {/* North terminal icon — credit card SVG */}
              <svg
                width="72"
                height="72"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#F4722B"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="5" width="20" height="14" rx="3" />
                <line x1="2" y1="10" x2="22" y2="10" />
                <line x1="6" y1="15" x2="10" y2="15" />
              </svg>
            </div>
          </div>

        </AnimatedSection>
      </div>
    </section>
  );
}
